import { useMemo } from 'react'
import { ScanSearch } from 'lucide-react'
import type { AdminColumn } from '@/components/DataTable/types'
import { SecCrudTable, type SecField } from './SecCrudTable'
import { SEC_STR as S, type VulnScanRow } from './securityMoreApi'

/**
 * A18.20 — Zaiflik skanerlari. Admin logs scan results into a registry
 * (`sec_vuln_scans`, 000248): target + scanner + severity + triage status,
 * `found_at` = when logged. Real CRUD via `/admin/security/vuln-scans` — the
 * admin records findings from any tool (or by hand) with no live bridge needed.
 */
const SEVERITY_OPTS = [
  { value: 'critical', label: 'critical' },
  { value: 'high', label: 'high' },
  { value: 'medium', label: 'medium' },
  { value: 'low', label: 'low' },
  { value: 'info', label: 'info' },
]
const STATUS_OPTS = [
  { value: 'open', label: 'open' },
  { value: 'triaged', label: 'triaged' },
  { value: 'fixed', label: 'fixed' },
  { value: 'accepted', label: 'accepted' },
]

export function VulnScansScreen() {
  const columns = useMemo<AdminColumn<VulnScanRow>[]>(
    () => [
      { key: 'target', header: S.target, type: 'text' },
      { key: 'scanner', header: S.scanner, type: 'text', width: 150 },
      {
        key: 'severity',
        header: S.severity,
        type: 'enum',
        width: 120,
        options: [
          { value: 'critical', label: S.critical, status: 'banned' },
          { value: 'high', label: S.high, status: 'blocked' },
          { value: 'medium', label: S.medium, status: 'review' },
          { value: 'low', label: S.low, status: 'pending' },
          { value: 'info', label: S.info, status: 'active' },
        ],
      },
      {
        key: 'status',
        header: S.status,
        type: 'enum',
        width: 130,
        options: [
          { value: 'open', label: S.openLbl, status: 'open' },
          { value: 'triaged', label: 'triaged', status: 'review' },
          { value: 'fixed', label: 'fixed', status: 'active' },
          { value: 'accepted', label: 'accepted', status: 'pending' },
        ],
      },
      { key: 'notes', header: S.notes, type: 'text', minWidth: 160 },
      { key: 'found_at', header: S.foundAt, type: 'date', width: 150 },
    ],
    [],
  )

  const fields = useMemo<SecField[]>(
    () => [
      { name: 'target', placeholder: S.target },
      { name: 'scanner', placeholder: S.scanner },
      { name: 'severity', placeholder: S.severity, type: 'select', options: SEVERITY_OPTS },
      { name: 'status', placeholder: S.status, type: 'select', options: STATUS_OPTS },
      { name: 'notes', placeholder: S.notes },
    ],
    [],
  )

  return (
    <SecCrudTable<VulnScanRow>
      screenId="A18.20"
      title={S.vulnScans}
      addLabel={S.addVuln}
      path="/admin/security/vuln-scans"
      listKey="scans"
      searchKeys={['target', 'scanner', 'severity', 'notes']}
      fields={fields}
      columns={columns}
      icon={<ScanSearch size={28} strokeWidth={1.5} />}
    />
  )
}
